import React, { useState, useEffect, useMemo } from "react";
import "./calculateTask3LG.less";

// 相态映射
const phaseMap: { [key: string]: string } = {
  V: "气相",
  L: "液相",
  VL: "气液混合",
};

// 单位选项
const temperatureUnits = ["℃", "K"];
const pressureUnits = ["Pa", "bar", "KPa", "MPa", "atm"];

interface Props {
  stream: any; // 单个流股数据（已合并设计值）
  input: any; // 输入数据
  isHide: boolean; // 是否隐藏设计值
}

const SingleStreamView: React.FC<Props> = ({ stream, input, isHide }) => {
  // 单位状态
  const [tempUnit, setTempUnit] = useState<string>("℃");
  const [pressUnit, setPressUnit] = useState<string>("MPa");
  const [materials, setMaterials] = useState<any[]>([]);

  // 初始化组分列表
  useEffect(() => {
    if (input?.inputs?.streams) {
      const m = input.inputs.streams.find((s: any) => s.materials?.length);
      setMaterials(m?.materials ?? []);
    }
  }, [input]);

  // 格式化数值
  const fmt = (v: any, n = 2) =>
    v == null || isNaN(Number(v)) ? "—" : Number(v).toFixed(n);

  // 获取温度值（适配单位）
  const getT = (s: any) => tempUnit === "℃" ? s.T_C : s.T_K;
  // 获取压力值（适配单位）
  const getP = (s: any) => s?.[`P_${pressUnit}`];

  // 计算组分对应4种数据的值（设计/计算，删除运行值）
  const getCompAllValues = (matIdx: number) => {
    const s = stream; // 计算值
    const d = stream.design; // 设计值

    // 1. 摩尔分率
    const moleFracDes = fmt(d.molar_frac?.[matIdx] ?? 0, 4);
    const moleFracCalc = fmt(s.molar_frac?.[matIdx] ?? 0, 4);

    // 2. 摩尔流量 (mol/h)
    const moleFlowDes = fmt((d.f_mol_per_h ?? 0) * (d.molar_frac?.[matIdx] ?? 0));
    const moleFlowCalc = fmt((s.f_mol_per_h ?? 0) * (s.molar_frac?.[matIdx] ?? 0));

    // 3. 质量流量 (kg/h)
    const massFlowDes = fmt((d.f_kg_per_h ?? 0) * (d.mass_frac?.[matIdx] ?? 0));
    const massFlowCalc = fmt((s.f_kg_per_h ?? 0) * (s.mass_frac?.[matIdx] ?? 0));

    // 4. 质量浓度 (mg/m³)
    const massConDes = fmt(
      ((d.f_kg_per_h ?? 0) * 1e6 * (d.mass_frac?.[matIdx] ?? 0)) / (d.f_nm3_per_h ?? 1)
    );
    const massConCalc = fmt(
      ((s.f_kg_per_h ?? 0) * 1e6 * (s.mass_frac?.[matIdx] ?? 0)) / (s.f_nm3_per_h ?? 1)
    );

    return {
      moleFrac: { des: moleFracDes, calc: moleFracCalc },
      moleFlow: { des: moleFlowDes, calc: moleFlowCalc },
      massFlow: { des: massFlowDes, calc: massFlowCalc },
      massConcentration: { des: massConDes, calc: massConCalc },
    };
  };

  // 工艺参数模块数据
  const processParams = useMemo(() => {
    const d = stream.design;
    const s = stream;

    return [
      {
        label: "摩尔流量 (mol/h)",
        design: fmt(d.f_mol_per_h),
        calc: fmt(s.f_mol_per_h),
        icon: "📊",
      },
      {
        label: "质量流量 (kg/h)",
        design: fmt(d.f_kg_per_h),
        calc: fmt(s.f_kg_per_h),
        icon: "⚖️",
      },
      {
        label: "焓 (J/mol)",
        design: fmt(d.H),
        calc: fmt(s.H),
        icon: "🔥",
      },
      {
        label: `温度 (${tempUnit})`,
        design: fmt(getT(d)),
        calc: fmt(getT(s)),
        icon: "🌡️",
      },
      {
        label: `压力 (${pressUnit})`,
        design: fmt(getP(d), 4),
        calc: fmt(getP(s), 4),
        icon: "🔧",
      },
      {
        label: "气化率",
        design: fmt(d.vf, 4),
        calc: fmt(s.vf, 4),
        icon: "💧",
      },
    ];
  }, [stream, tempUnit, pressUnit]);

  return (
    <div className="single-stream-view-container">
      {/* 流股标题 + 相态 */}
      <div className="stream-header">
        <h2 className="stream-title">{stream.name} 流股数据</h2>
        <span className="stream-phase">相态：{phaseMap[stream.phase] ?? stream.phase ?? "未知"}</span>
      </div>

      {/* 单位切换器 */}
      <div className="unit-selector-container">
        <div className="unit-selector">
          <span className="unit-selector-label">温度单位切换：</span>
          <div className="unit-buttons">
            {temperatureUnits.map((u) => (
              <button
                key={u}
                className={`unit-button ${tempUnit === u ? "active" : ""}`}
                onClick={() => setTempUnit(u)}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
        <div className="unit-selector pressure-selector">
          <span className="unit-selector-label">压力单位切换：</span>
          <div className="unit-buttons">
            {pressureUnits.map((u) => (
              <button
                key={u}
                className={`unit-button ${pressUnit === u ? "active" : ""}`}
                onClick={() => setPressUnit(u)}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 工艺参数卡片区域 */}
      <div className="process-params-grid">
        {processParams.map((param, idx) => (
          <div key={idx} className="param-card">
            <div className="param-icon">{param.icon}</div>
            <div className="param-content">
              <div className="param-label">{param.label}</div>
              <div className="param-values">
                {isHide ? (<span className="param-calc">{param.calc}</span>) : (<>
                  <span className="param-design">设计值：{param.design}</span>
                  <span className="param-calc">计算值：{param.calc}</span>
                </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 组分数据表格（双层表头，4种数据全部展示） */}
      <div className="component-table-section">
        <div className="table-container">
          <div className="table-scroll-wrapper">
            <table className="component-data-table">
              <thead>
                {/* 第一层主表头：数据类型 */}
                <tr className="table-header-main">
                  <th rowSpan={2}>组分</th>
                  <th colSpan={isHide ? 1 : 2}>摩尔分率</th>
                  <th colSpan={isHide ? 1 : 2}>摩尔流量 (mol/h)</th>
                  <th colSpan={isHide ? 1 : 2}>质量流量 (kg/h)</th>
                  <th colSpan={isHide ? 1 : 2}>质量浓度 (mg/m³)</th>
                </tr>
                {/* 第二层子表头：设计/计算 */}
                {isHide ? null : (<>
                  <tr className="table-header-sub">
                    <th>设计值</th><th>计算值</th>
                    <th>设计值</th><th>计算值</th>
                    <th>设计值</th><th>计算值</th>
                    <th>设计值</th><th>计算值</th>
                  </tr>
                </>)}
              </thead>
              <tbody>
                {materials.length > 0 ? (
                  materials.map((mat, idx) => {
                    const values = getCompAllValues(idx);
                    return (
                      <>
                        {isHide ? <>
                          <tr key={idx} className="table-row">
                            <td>{mat.name}</td>
                            {/* 摩尔分率 */}
                            <td>{values.moleFrac.calc}</td>
                            {/* 摩尔流量 */}
                            <td>{values.moleFlow.calc}</td>
                            {/* 质量流量 */}
                            <td>{values.massFlow.calc}</td>
                            {/* 质量浓度 */}
                            <td>{values.massConcentration.calc}</td>
                          </tr>
                        </> : (<>
                          <tr key={idx} className="table-row">
                            <td>{mat.name}</td>
                            {/* 摩尔分率 */}
                            <td>{values.moleFrac.des}</td>
                            <td>{values.moleFrac.calc}</td>
                            {/* 摩尔流量 */}
                            <td>{values.moleFlow.des}</td>
                            <td>{values.moleFlow.calc}</td>
                            {/* 质量流量 */}
                            <td>{values.massFlow.des}</td>
                            <td>{values.massFlow.calc}</td>
                            {/* 质量浓度 */}
                            <td>{values.massConcentration.des}</td>
                            <td>{values.massConcentration.calc}</td>
                          </tr>
                        </>)}
                      </>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="empty-cell">暂无组分数据</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleStreamView;